/**
 * Created by jonas on 2021-02-02.
 */
import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {Loner} from "./Loner";
import {LonerGameMode} from "./server/LonerGameMode";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {LonerStatics} from "./LonerStatics";
import {
    BigWildWinAnimationHighlightData,
    IBetWayWinData,
    LonerData,
    LonerGameData,
    LonerInitData,
    mx2Data,
    nextDuck,
    nextPlayerInteraction,
    PinTheWinFeatureObj,
    shotData,
    xSplitInfo,
    xWaysInfo
} from "./server/LonerData";
import {LonerSymbolStates, SYMBOL_NAMES} from "../core/reel/ReelInterfaces";
import {BetFeature} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetFeatureController";


export class LonerModel extends GameModel<LonerInitData, LonerGameData> {
    private static RANDOM_REELSET:string[] = ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"];
    private _playerFSSelection:number;
    private _preWinSymbols:boolean[][]; // used for pre win animation on symbol overlay.
    private _betWayWins:IBetWayWinData[] = [];
    private _originalDataReels:string[][];
    private _isRestore:boolean;
    private _winRatio:number;
    private _brokeBank:boolean;
    private _playedBetValue:number;
    private _upToMaxWinAmount:number;
    private _accumulatedRoundWin:number = 0;
    private _totalSpinWinnings:number;
    private _reels:string[][];
    private _allWinSymPositions:boolean[][];
    private _totalWaysWon:number;
    private _evaluatedArea:string[][];
    private _wasFeatureBuy:boolean;
    private _freespinsLeft:number;
    private _currentBonusGameWin:number;
    private _numberOfFreespinsPlayed:number;
    private _scattersLanded:number[];
    private _allScattersLanded:number;
    private _scatterAnticipationReels:boolean[];
    private _scatterTransform:LonerSymbolStates[];
    private _multiplier:number;
    private _stickyWildMultiplier:number[];
    private _boostedBetPlayed:boolean;
    private _winMultiplier:number;
    private _featureString:number;
    private _timesBetWinDraw:number;
    private _winRange:string[];
    private _winValues:number[];
    private _shotData:shotData;
    private _featureTrigger:string;
    private _reelsEnteringFreespin:string[][];
    private _bigWildSize:number = -1;
    private _bigWildTeaseSize:number;
    private _xSplitInfo:xSplitInfo[];
    private _xWaysInfo:xWaysInfo[];
    private _duckReels:string[][];
    private _duckValues:number[][];
    private _addDucksTopReel:nextDuck[];
    private _addDucksMiddleReel:nextDuck[];
    private _addDucksBottomReel:nextDuck[];
    private _totalBonusWin:number;
    private _instantTVReveal:boolean[];
    private _nextPlayerInteraction:nextPlayerInteraction;
    private _bigWildOnReels:boolean[];
    private _bigWildReelPosition:number = -1;
    private _bigWildTeaseReelPosition:number;
    private _pinTheWinFeatureInfo:PinTheWinFeatureObj;
    private _mode:string;
    private _nextMode:string;
    private _previousMX2Data:mx2Data[];
    private _currentMX2Data:mx2Data[];
    private _bigWildWinAnimationHighlightData:BigWildWinAnimationHighlightData;
    private _featureName:string = "";
    private _symbolSizes:number[][];

    constructor(api:ApiPlugin, triggerEvents:boolean = true) {
        api.events.once("featureName", (data:string) => this.onFeatureName(data));
        super(api, triggerEvents);
    }

    private onFeatureName(name:string):void {
        this._featureName = name;
    }

    protected onInit(data:LonerInitData):void {
        this._originalDataReels = JSON.parse(JSON.stringify(data.reels));
        this._playedBetValue = parseFloat(Loner.apiPlugin.freeBets.hasFreeBets() ? Loner.apiPlugin.freeBets.getBet() : Loner.apiPlugin.betLevel.getLevel());
        this._nextMode = data.nextMode;
        this._mode = data.mode;

        this._pinTheWinFeatureInfo = {
            startMovementFlag : false,
            retorePos : this.getPinTheWinRestorePos(data)
        }

        if(data.mode == LonerGameMode.NORMAL || data.mode == LonerGameMode.FEATURE_PIN_THE_WIN) {
            this._reels = this.addRandomSymbolsToReelset(data.reels, data.isRestoreState);

            data.reels = this._reels;
            if(data.reelsEnteringFreespin) {
                this._reelsEnteringFreespin = this.addRandomSymbolsToReelset(data.reelsEnteringFreespin);
            }
        }

        super.onInit(data);

        this._symbolSizes = data.symbolSizes;

        if(data.featureInfo) {
            if(data.featureInfo.MX2InfoNextMovement) {
                if(!this._previousMX2Data) {
                    this._previousMX2Data = data.featureInfo.MX2InfoNextMovement;
                } else {
                    this._previousMX2Data = [];
                    for(let i:number = 0; i < this._currentMX2Data.length; i++) {
                        this._previousMX2Data.push({
                            nextValue : this._currentMX2Data[i].nextValue,
                            currentValue : this._currentMX2Data[i].currentValue,
                            nextPosition : this._currentMX2Data[i].nextPosition,
                            currentPosition : this._currentMX2Data[i].currentPosition
                        })
                    }
                }

                this._currentMX2Data = data.featureInfo.MX2InfoNextMovement;
            }
            if(data.featureInfo.firstDuckReels) {
                this._duckReels = this.parseDuckReels(data.featureInfo.firstDuckReels);
                this._addDucksTopReel = [];
                this._addDucksMiddleReel = [];
                this._addDucksBottomReel = [];
                this.parseNextDucks(data.featureInfo.firstDuckReels, data.featureInfo.duckValues);
            }
            if(data.featureInfo.duckValues) {
                this._duckValues = this.parseDuckValues(data.featureInfo.duckValues);
            }
            if(data.featureInfo.totalBonusWin) {
                this._totalBonusWin = data.featureInfo.totalBonusWin;
            }
        }

        if(data.isRestoreState == true && (data.mode == LonerGameMode.FEATURE_SHOOT_SHOOT_DUCK || (data.mode == LonerGameMode.NORMAL && data.nextMode == LonerGameMode.FEATURE_SHOOT_SHOOT_DUCK))) {
            this._accumulatedRoundWin = data.featureInfo.BaseWin;
        } else {
            this._accumulatedRoundWin = data.accumulatedRoundWin;
        }

        if(data.mode == LonerGameMode.FEATURE_SHOOT_SHOOT_DUCK && data.isRestoreState == true) {
            this._duckReels = this.parseDuckReels(data.reels);
            this._reelsEnteringFreespin = this.addRandomSymbolsToReelset(data.reelsEnteringFreespin);
            this._addDucksTopReel = [];
            this._addDucksMiddleReel = [];
            this._addDucksBottomReel = [];
            this.parseNextDucks(data.reels, data.featureInfo.duckValues);
            this._totalBonusWin = data.featureInfo.totalBonusWin;
        }

        this._isRestore = data.isRestoreState;
        this._winMultiplier = data.winMultiplier;
        this._boostedBetPlayed = data.boostedBetPlayed;
        this._brokeBank = data.brokeTheBank;
        this._evaluatedArea = data.evaluatedArea;
        this._freespinsLeft = data.freespinsLeft;
        this._scattersLanded = this.parseScatterLanded(data);
        this._allScattersLanded = this.parseAllScattersLanded();
        this._multiplier = this.parseMultiplier(data);
        this._stickyWildMultiplier = data.stickyWildMultiplier;
        this._currentBonusGameWin = data.currentBonusGameWin;
    }

    public onGame(data:LonerGameData):void {
        this.makeBigWildWinAnimationHighlightData(data);
        this._originalDataReels = JSON.parse(JSON.stringify(data.reels));
        this._preWinSymbols = this.calculatePreWinSymbols(data);
        this._wasFeatureBuy = data.wasFeatureBuy;
        this._betWayWins = this.parseBetWayWins(data);
        this.parseAllWinSymbolPositions(data);
        this._nextMode = data.nextMode;
        this._mode = data.mode;
        this._symbolSizes = data.symbolSizes;

        if(data.mode == LonerGameMode.NORMAL && data.nextMode == LonerGameMode.FEATURE_SHOOT_SHOOT_DUCK) {
            this._addDucksTopReel = [];
            this._addDucksMiddleReel = [];
            this._addDucksBottomReel = [];
        }

        if(data.mode == LonerGameMode.FEATURE_SHOOT_SHOOT_DUCK) {
            this.parseNextDucks(data.reels, data.featureInfo.duckValues);
            this._totalBonusWin = data.featureInfo.totalBonusWin;
        } else if(data.mode == LonerGameMode.NORMAL) {
            //below check is used to prevent adding extra symbols when bonus mode of action spins is played in main game.
            if(!Loner.isActionSpinsActive) {
                this._reels = this.addRandomSymbolsToReelset(data.reels);
                data.reels = this._reels;
            }
        }

        if(data.currentBonusGameWin) {
            this._currentBonusGameWin = data.currentBonusGameWin;
        }
        if(data.numberOfFreespinsPlayed) {
            this._numberOfFreespinsPlayed = data.numberOfFreespinsPlayed;
        }

        this._featureTrigger = "";
        this._xWaysInfo = [];
        this._xSplitInfo = [];
        this._bigWildSize = -1;
        this._bigWildTeaseSize = -1;
        this._bigWildReelPosition = -1;
        if(data.featureInfo) {
            if(data.featureInfo.MX2InfoNextMovement) {
                this._previousMX2Data = [];

                if(!this._currentMX2Data) {
                    this._currentMX2Data = data.featureInfo.MX2InfoNextMovement;
                }

                for(let i:number = 0; i < this._currentMX2Data.length; i++) {
                    this._previousMX2Data.push({
                        nextValue : this._currentMX2Data[i].nextValue,
                        currentValue : this._currentMX2Data[i].currentValue,
                        nextPosition : this._currentMX2Data[i].nextPosition,
                        currentPosition : this._currentMX2Data[i].currentPosition
                    })
                }

                this._currentMX2Data = data.featureInfo.MX2InfoNextMovement;
            }
            if(data.featureInfo.SpinsPlayed) {

            }
            if(data.featureInfo.reelsEnteringFreespin && !Loner.isActionSpinsActive) {
                this._reelsEnteringFreespin = this.addRandomSymbolsToReelset(data.featureInfo.reelsEnteringFreespin);
            }
            this._playerFSSelection = data.featureInfo.playerFSSelection;
            this._timesBetWinDraw = data.featureInfo.timesBetWinDraw;
            this._winRange = data.featureInfo.winRange;
            this._winValues = data.featureInfo.winValues;
            if(data.featureInfo.firstDuckReels) {
                this._duckReels = this.parseDuckReels(data.featureInfo.firstDuckReels);
                this.parseNextDucks(data.featureInfo.firstDuckReels, data.featureInfo.duckValues);
                this._totalBonusWin = data.featureInfo.totalBonusWin;
            }
            if(data.featureInfo.duckValues) {
                this._duckValues = this.parseDuckValues(data.featureInfo.duckValues);
            }
            if(data.featureInfo.shotInfo) {
                data.featureInfo.shotInfo.forEach(item => {
                    this._shotData = {
                        awardMultiplier : item.awardMultiplier,
                        position : item.position,
                        symbolShot : item.symbolShot
                    };
                })
            }
            if(data.featureInfo.featureTrigger) {
                this._featureTrigger = data.featureInfo.featureTrigger;
            }
            if(data.featureInfo.WildSpin) {
                for(let i:number = 0; i < data.featureInfo.WildSpin.length; i++) {
                    if(data.featureInfo.WildSpin[i] == "W2") {
                        if((i == 0 && data.featureInfo.WildSpin[i + 1] == "X") || (i == 4 && data.featureInfo.WildSpin[i - 1] == "X")) {
                            this._bigWildTeaseSize = 2;
                        } else {
                            this._bigWildSize = 2;
                        }
                    }
                    if(data.featureInfo.WildSpin[i] == "W3") {
                        if((i == 0 && data.featureInfo.WildSpin[i + 1] == "X") || (i == 4 && data.featureInfo.WildSpin[i - 1] == "X")) {
                            this._bigWildTeaseSize = 3;
                        } else {
                            this._bigWildSize = 3;
                        }
                    }
                }
                this.parseReelsForBigWilds(data.featureInfo.WildSpin);
            }

            if(data.featureInfo.ScatterReveal) {
                for(let i:number = 0; i < data.featureInfo.ScatterReveal.length; i++) {
                    if(data.featureInfo.ScatterReveal[i].feature == "XWays") {
                        this._xWaysInfo.push({
                            pos : data.featureInfo.ScatterReveal[i].position,
                            symConvertedTo : data.featureInfo.xWaysRevealSymbol as SYMBOL_NAMES
                        });
                    }

                    if(data.featureInfo.ScatterReveal[i].feature == "XSPLIT") {
                        this._xSplitInfo.push({
                            pos : data.featureInfo.ScatterReveal[i].position
                        });
                    }
                }
            }
        }

        if(data.mode === LonerGameMode.FEATURE_PIN_THE_WIN) {
            this._pinTheWinFeatureInfo = {
                ...this._pinTheWinFeatureInfo,
                bonusWin : data.featureInfo.BonusWin,
                cornerValue : data.featureInfo.CornerValues,
                nextCornerValues : data.featureInfo.NextCornerValues,
                spinsPlayed : data.featureInfo.SpinsPlayed,
                nextWinMulti : data.featureInfo.NextWinMultiplier,
                startMovementFlag : true,
                destReelId : this.getPinTheWinDesReelSymId(data)[0],
                destSymId : this.getPinTheWinDesReelSymId(data)[1],
                winMultiplierUpdate : data.featureInfo.WinMultiplierUpdate
            }
        }

        if(data.mode === LonerGameMode.NORMAL && data.nextMode === LonerGameMode.FEATURE_PIN_THE_WIN) {
            this._pinTheWinFeatureInfo = {
                startMovementFlag : false,
            }
        }
        this._bigWildOnReels = this.parseBigWildOnReels();

        this._isRestore = false;
        this._featureString = data.featureString;
        this._evaluatedArea = data.evaluatedArea;
        this._brokeBank = data.brokeTheBank;
        this._playedBetValue = data.playedBetValue;
        this._accumulatedRoundWin = data.accumulatedRoundWin;
        this._totalSpinWinnings = data.totalSpinWinnings;
        this._winMultiplier = data.winMultiplier;
        if(this._brokeBank == true) {
            this.parseWinOnMaxWin(data);
        }
        this.parseTotalWaysWon(this._betWayWins);
        this._freespinsLeft = data.freespinsLeft;
        this._scattersLanded = this.parseScatterLanded(data);
        this._allScattersLanded = this.parseAllScattersLanded();
        this._scatterAnticipationReels = this.parseScatterAnticipationReels();
        this._scatterTransform = this.parseScatterTransform(data);
        this.parseInstantTVReveal();
        this._stickyWildMultiplier = data.stickyWildMultiplier;
        this._nextPlayerInteraction = data.nextPlayerInteraction;

        super.onGame(data);
    }

    public parseBetWayWins(data:LonerGameData):IBetWayWinData[] {
        const retVal:IBetWayWinData[] = data.betWayWins;

        retVal.forEach((newBetWayWinData:IBetWayWinData) => {
            newBetWayWinData.reelWinPositions = this.calculateReelWinPos(data.evaluatedArea, newBetWayWinData.symbol, newBetWayWinData.length);
        });

        return retVal;
    }

    private makeBigWildWinAnimationHighlightData(data:LonerGameData):void {
        if(data.featureInfo && data.featureInfo.WildSpin) {
            const bigWildSpinArray:string[] = JSON.parse(JSON.stringify(data.featureInfo.WildSpin)),
                evaluatedArea:string[][] = data.evaluatedArea;
            bigWildSpinArray.pop();
            bigWildSpinArray.shift();
            if(bigWildSpinArray.indexOf("W3") > -1) {
                this._bigWildWinAnimationHighlightData = {
                    bigWildSize : 3,
                    bigWinPresentOnReels : [1, 2, 3],
                    bigWinPresentOnSymbols : []
                }
                for(let symIdx:number = 0; symIdx < 3; symIdx++) {
                    if(bigWildSpinArray[symIdx] === "W3" && this._bigWildWinAnimationHighlightData.bigWinPresentOnSymbols) {
                        this._bigWildWinAnimationHighlightData.bigWinPresentOnSymbols.push(symIdx);
                    }
                }
            } else {
                this._bigWildWinAnimationHighlightData = {
                    bigWildSize : 2,
                    bigWinPresentOnReels : [],
                    bigWinPresentOnSymbols : []
                }
                for(let symIdx:number = bigWildSpinArray.indexOf("W2"); symIdx < 3; symIdx++) {
                    if(bigWildSpinArray[symIdx] === "W2" && this._bigWildWinAnimationHighlightData.bigWinPresentOnSymbols) {
                        this._bigWildWinAnimationHighlightData.bigWinPresentOnSymbols.push(symIdx);
                    }
                    if(evaluatedArea[2][symIdx] === "W" && !this.isFeatureTurnedWild(2, symIdx, data)) {
                        if(evaluatedArea[1][symIdx] === "W" && !this.isFeatureTurnedWild(1, symIdx, data)) {

                            this._bigWildWinAnimationHighlightData.bigWinPresentOnReels?.push(1, 2);
                        } else if(evaluatedArea[1][symIdx] === "W" && !this.isFeatureTurnedWild(3, symIdx, data)) {
                            this._bigWildWinAnimationHighlightData.bigWinPresentOnReels?.push(2, 3);
                        }
                    }
                }
            }
        } else {
            this._bigWildWinAnimationHighlightData = {};
        }
    }

    private isFeatureTurnedWild(reelIdx:number, symIdx:number, data:LonerGameData):boolean {
        if(data.featureInfo && data.featureInfo.ScatterReveal && data.featureInfo.ScatterReveal.length > 0) {
            for(let scatterRevealIdx:number = 0; scatterRevealIdx < data.featureInfo.ScatterReveal.length; ++scatterRevealIdx) {
                const individualScatterReveal = data.featureInfo.ScatterReveal[scatterRevealIdx];
                if(individualScatterReveal.feature === "XSPLIT" || individualScatterReveal.feature === "WILD") {
                    if(individualScatterReveal.position[0] === reelIdx && individualScatterReveal.position[1] === symIdx) {
                        return true;
                    }
                }
            }
        }
        return false;
    }


    private getPinTheWinRestorePos(data:LonerInitData):number[] {
        const pos:number[] = [2, 2];
        if(this._mode === LonerGameMode.FEATURE_PIN_THE_WIN && this._nextMode === LonerGameMode.FEATURE_PIN_THE_WIN) {
            for(let reelIdx:number = 0; reelIdx < data.reels.length; reelIdx++) {
                for(let symIdx:number = 0; symIdx < data.reels[reelIdx].length; symIdx++) {
                    if(data.reels[reelIdx][symIdx] === "S") {
                        pos[0] = reelIdx;
                        pos[1] = symIdx;
                        break;
                    }
                }
            }
        }
        return pos;
    }

    private getPinTheWinDesReelSymId(data:LonerGameData):number[] {
        for(let reelId:number = 0; reelId < data.reels.length; reelId++) {
            for(let symId:number = 0; symId < data.reels[reelId].length; symId++) {
                if(data.reels[reelId][symId] === "S") {
                    return [reelId, symId];
                }
            }
        }
        return []
    }

    private parseInstantTVReveal():void {
        this._instantTVReveal = [false, false, false, false, false];
        if(this._allScattersLanded > 0 && this.featureTrigger !== LonerStatics.MAX_WIN_TEASE_TRIGGER && this.featureTrigger !== LonerStatics.MAX_WIN_TRIGGER) {
            if(this._scattersLanded[0] == -1) {
                if(this._scattersLanded[2] > -1) {
                    this._instantTVReveal[2] = true;
                }
                if(this._scattersLanded[4] > -1) {
                    this._instantTVReveal[4] = true;
                }
            }
            if(this._scattersLanded[0] == -1 && this._scattersLanded[2] == -1) {
                this._instantTVReveal[4] = true;
            }
        } else {
            this._instantTVReveal[0] = true;
            this._instantTVReveal[2] = true;
        }
    }

    public getCurrentMX2Data(reel:number, row:number):mx2Data {
        for(let i:number = 0; i < this._currentMX2Data.length; i++) {
            if(this._currentMX2Data[i].currentPosition[0] == reel && this._currentMX2Data[i].currentPosition[1] == row) {
                return this._currentMX2Data[i];
            }
        }

        throw new Error("No MX2 value on reel: " + reel + " row: " + row);
    }

    public getCurrentMX2DataNextPosition(reel:number, row:number):mx2Data {
        for(let i:number = 0; i < this._currentMX2Data.length; i++) {
            if(this._currentMX2Data[i].nextPosition[0] == reel && this._currentMX2Data[i].nextPosition[1] == row) {
                return this._currentMX2Data[i];
            }
        }

        throw new Error("No MX2 Next Position value on reel: " + reel + " row: " + row);
    }

    public getPreviousMX2Data(reel:number, row:number):mx2Data {
        for(let i:number = 0; i < this._previousMX2Data.length; i++) {
            if(this._previousMX2Data[i].currentPosition[0] == reel && this._previousMX2Data[i].currentPosition[1] == row) {
                return this._previousMX2Data[i];
            }
        }

        throw new Error("No MX2 value on reel: " + reel + " row: " + row);
    }

    private parseNextDucks(reels:string[][], multipliers:number[][]):void {
        this._addDucksTopReel.push({duckName : reels[4][0], multiplier : multipliers[4][0]});
        this._addDucksMiddleReel.push({duckName : reels[0][1], multiplier : multipliers[0][1]});
        this._addDucksBottomReel.push({duckName : reels[4][2], multiplier : multipliers[4][2]});
    }

    private parseDuckReels(reels:string[][]):string[][] {
        const parsedReels:string[][] = [];
        let topReel:string[] = [];
        let middleReel:string[] = [];
        let bottomReel:string[] = [];
        for(let i:number = 1; i < reels.length - 1; i++) {
            topReel.push(reels[i][0]);
            middleReel.push(reels[i][1]);
            bottomReel.push(reels[i][2]);
        }
        parsedReels.push(topReel);
        parsedReels.push(middleReel);
        parsedReels.push(bottomReel);

        return parsedReels;
    }

    private parseDuckValues(values:number[][]):number[][] {
        const parsedValues:number[][] = [];
        let topReelValues:number[] = [];
        let middleReelValues:number[] = [];
        let bottomReelValues:number[] = [];
        for(let i:number = 1; i < values.length - 1; i++) {
            topReelValues.push(values[i][0]);
            middleReelValues.push(values[i][1]);
            bottomReelValues.push(values[i][2]);
        }
        parsedValues.push(topReelValues);
        parsedValues.push(middleReelValues);
        parsedValues.push(bottomReelValues);

        return parsedValues;
    }

    private parseBigWildOnReels():boolean[] {
        const wildOnReels:boolean[] = [false, false, false, false, false];
        if(this._bigWildSize > 0 || this._bigWildTeaseSize > 0) {
            wildOnReels[1] = true;
            wildOnReels[2] = true;
            if(this._bigWildSize == 3 || this._bigWildTeaseSize == 3) {
                wildOnReels[3] = true;
            }
        }
        return wildOnReels;
    }

    private parseReelsForBigWilds(wildString:string[]):void {
        let topRightPosition:number = -1;
        let topRightTeasePosition:number = -1;
        let nineSymbolWildString:string[] = ["X", "X"].concat(wildString).concat(["X", "X"]);
        if(wildString[0] == "W2" && wildString[1] == "X") {
            nineSymbolWildString[1] = "W2";
        }
        if(wildString[4] == "W2" && wildString[3] == "X") {
            nineSymbolWildString[7] = "W2";
        }
        if(wildString[0] == "W3" && wildString[1] == "X") {
            nineSymbolWildString[0] = "W3";
            nineSymbolWildString[1] = "W3";
        }
        if(wildString[1] == "W3" && wildString[2] == "X") {
            nineSymbolWildString[1] = "W3";
        }
        if(wildString[2] == "X" && wildString[3] == "W3") {
            nineSymbolWildString[7] = "W3";
        }
        if(wildString[3] == "X" && wildString[4] == "W3") {
            nineSymbolWildString[7] = "W3";
            nineSymbolWildString[8] = "W3";
        }

        for(let j:number = 0; j < nineSymbolWildString.length; j++) {
            if(nineSymbolWildString[j] == "W2") {
                //Checking tease wild
                if((j == 1 || j == 6) && topRightTeasePosition == -1) {
                    topRightTeasePosition = j;
                    j += 2;
                } else if(j > 1 && j < 6 && topRightPosition == -1) {
                    //Checking normal wild
                    topRightPosition = j;
                    j += 2;
                }
            }
            if(nineSymbolWildString[j] == "W3") {
                //Checking tease wild
                if((j == 0 || j == 6) && topRightTeasePosition == -1) {
                    topRightTeasePosition = j;
                    j += 3;
                    //Checking normal wild
                } else if(j !== 0 && j !== 6 && topRightPosition == -1) {
                    topRightPosition = j;
                    j += 3;
                }
            }
        }

        //Setting Wild on bottom right position
        if(this._bigWildSize == 2) {
            this._bigWildReelPosition = topRightPosition + 1;
            this._reels[2][this._bigWildReelPosition] = SYMBOL_NAMES.wild2X2;
            this._reels[2][topRightPosition] += "_HIDDEN";
            this._reels[1][topRightPosition] += "_HIDDEN";
            this._reels[1][topRightPosition + 1] += "_HIDDEN";
        } else if(this._bigWildSize == 3) {
            this._bigWildReelPosition = topRightPosition + 2;
            this._reels[3][this._bigWildReelPosition] = SYMBOL_NAMES.wild3X3;
            this._reels[3][topRightPosition] += "_HIDDEN";
            this._reels[3][topRightPosition + 1] += "_HIDDEN";
            this._reels[2][topRightPosition] += "_HIDDEN";
            this._reels[2][topRightPosition + 1] += "_HIDDEN";
            this._reels[2][topRightPosition + 2] += "_HIDDEN";
            this._reels[1][topRightPosition] += "_HIDDEN";
            this._reels[1][topRightPosition + 1] += "_HIDDEN";
            this._reels[1][topRightPosition + 2] += "_HIDDEN";
        }
        if(this._bigWildTeaseSize == 2) {
            this._bigWildTeaseReelPosition = topRightTeasePosition + 1;
            this._reels[2][this._bigWildTeaseReelPosition] = SYMBOL_NAMES.wild2X2;
            this._reels[2][topRightTeasePosition] += "_HIDDEN";
            this._reels[1][topRightTeasePosition] += "_HIDDEN";
            this._reels[1][topRightTeasePosition + 1] += "_HIDDEN";
        } else if(this._bigWildTeaseSize == 3) {
            this._bigWildTeaseReelPosition = topRightTeasePosition + 2;
            this._reels[3][this._bigWildTeaseReelPosition] = SYMBOL_NAMES.wild3X3;
            this._reels[3][topRightTeasePosition] += "_HIDDEN";
            this._reels[3][topRightTeasePosition + 1] += "_HIDDEN";
            this._reels[2][topRightTeasePosition] += "_HIDDEN";
            this._reels[2][topRightTeasePosition + 1] += "_HIDDEN";
            this._reels[2][topRightTeasePosition + 2] += "_HIDDEN";
            this._reels[1][topRightTeasePosition] += "_HIDDEN";
            this._reels[1][topRightTeasePosition + 1] += "_HIDDEN";
            this._reels[1][topRightTeasePosition + 2] += "_HIDDEN";
        }
    }

    private getRawReelsData(data:LonerGameData):string[][] {
        const rawReels:string[][] = data.reels,
            reelsData:string[][] = [];
        for(let reelId:number = 0; reelId < LonerStatics.REEL_NUMBERS; reelId++) {
            reelsData[reelId] = [];
            const currReel:string[] = rawReels[reelId].slice(1, -1);
            for(let symId:number = 0; symId < 3; symId++) {
                reelsData[reelId][symId] = currReel[symId];
            }
        }
        return reelsData;
    }

    private isWildPresetOnReel(reelData:string[]):boolean {
        return reelData.includes("W");
    }

    private calculatePreWinSymbols(data:LonerGameData):boolean[][] {
        const reelsData:string[][] = this.getRawReelsData(data),
            midReelId:number = 2,
            reel0:string[] = reelsData[0],
            reel1:string[] = reelsData[1],
            reel2:string[] = reelsData[midReelId],
            reel3:string[] = reelsData[3],
            reel4:string[] = reelsData[4],
            isWildPresentOnReel0:boolean = this.isWildPresetOnReel(reelsData[0]),
            isWildPresentOnReel1:boolean = this.isWildPresetOnReel(reelsData[1]),
            isWildPresentOnReel2:boolean = this.isWildPresetOnReel(reelsData[midReelId]),
            isWildPresentOnReel3:boolean = this.isWildPresetOnReel(reelsData[3]),
            isWildPresentOnReel4:boolean = this.isWildPresetOnReel(reelsData[4]);

        let possibleWinSymbols:string[];

        if(isWildPresentOnReel2) {
            possibleWinSymbols = Array.from(new Set(reel1.filter(symName => reel0.includes(symName))));
        } else {
            const firstThreeReels:string[][] = [reel0, reel1, reel2];
            possibleWinSymbols = Array.from(new Set(firstThreeReels.reduce((a, b) => a.filter(c => b.includes(c)))));
        }

        return (this.fetchPreWinData(possibleWinSymbols, reelsData));
    }

    private fetchPreWinData(possibleWinSymbols:string[], reelsData:string[][]):boolean[][] {
        const preWinPos:boolean[][] = ArrayHelper.initArrayWithValues<boolean[]>(LonerStatics.REEL_NUMBERS, (index:number) => {
                return ArrayHelper.initArrayWithValues<boolean>(reelsData[index].length, () => false);
            }),
            midReelId:number = 2;

        if(possibleWinSymbols.length > 0) {
            for(let reelId:number = 0; reelId < reelsData.length; reelId++) {
                const currReelData:string[] = reelsData[reelId];
                if(possibleWinSymbols.length > 0) {
                    if(reelId > midReelId) {
                        possibleWinSymbols = Array.from(new Set(possibleWinSymbols.filter(symName => currReelData.includes(symName))));
                    }
                    for(let symId:number = 0; symId < 4; symId++) {
                        const currSymbol:string = currReelData[symId];
                        if(possibleWinSymbols.includes(currSymbol)) {
                            preWinPos[reelId][symId] = true;
                        }
                    }
                }
            }
        }

        return preWinPos;
    }

    private parseMultiplier(data:LonerData):number {
        let multiplier:number = 0;
        if(data.stickyWildMultiplier) {
            for(let i:number = 0; i < data.stickyWildMultiplier.length; i++) {
                multiplier += data.stickyWildMultiplier[i];
            }
            return multiplier;
        }

        return multiplier;
    }

    private parseScatterTransform(data:LonerData):LonerSymbolStates[] {
        const scattersTransform:LonerSymbolStates[] = [LonerSymbolStates.NO_WIN, LonerSymbolStates.NO_WIN, LonerSymbolStates.NO_WIN, LonerSymbolStates.NO_WIN, LonerSymbolStates.NO_WIN];
        if(data.featureInfo) {
            if(data.featureInfo.ScatterReveal) {
                for(let i:number = 0; i < data.featureInfo.ScatterReveal.length; i++) {
                    if(data.featureInfo.ScatterReveal[i].feature == "WILD") {
                        scattersTransform[data.featureInfo.ScatterReveal[i].position[0]] = LonerSymbolStates.SCATTER_WILD;
                    }
                    if(data.featureInfo.ScatterReveal[i].feature == "XSPLIT") {
                        scattersTransform[data.featureInfo.ScatterReveal[i].position[0]] = LonerSymbolStates.SCATTER_XSPLIT;
                    }
                    if(data.featureInfo.ScatterReveal[i].feature == "XWays") {
                        scattersTransform[data.featureInfo.ScatterReveal[i].position[0]] = LonerSymbolStates.SCATTER_XWAYS;
                    }
                    if(data.featureInfo.ScatterReveal[i].feature == "Max Win") {
                        scattersTransform[data.featureInfo.ScatterReveal[i].position[0]] = LonerSymbolStates.SCATTER_MAX_WIN;
                    }
                }
            }
            if(data.mode == LonerGameMode.NORMAL && data.nextMode == LonerGameMode.FEATURE_TICK_TICK_BOOM) {
                scattersTransform[0] = LonerSymbolStates.SCATTER_TICK;
                scattersTransform[2] = LonerSymbolStates.SCATTER_TICK_TICK;
                scattersTransform[4] = LonerSymbolStates.SCATTER_BOOM;
            } else if(data.mode == LonerGameMode.NORMAL && data.nextMode == LonerGameMode.FEATURE_PIN_THE_WIN) {
                scattersTransform[0] = LonerSymbolStates.SCATTER_PIN;
                scattersTransform[2] = LonerSymbolStates.SCATTER_THE;
                scattersTransform[4] = LonerSymbolStates.SCATTER_WIN;
            } else if(data.mode == LonerGameMode.NORMAL && data.nextMode == LonerGameMode.FEATURE_SHOOT_SHOOT_DUCK) {
                scattersTransform[0] = LonerSymbolStates.SCATTER_SHOOT;
                scattersTransform[2] = LonerSymbolStates.SCATTER_SHOOT_SHOOT;
                scattersTransform[4] = LonerSymbolStates.SCATTER_DUCK;
            } else if(data.featureInfo.featureTrigger == LonerStatics.MAX_WIN_TEASE_TRIGGER) {
                scattersTransform[0] = LonerSymbolStates.M;
                scattersTransform[2] = LonerSymbolStates.A;
                scattersTransform[4] = LonerSymbolStates.SAD;
            } else if(data.featureInfo.featureTrigger == LonerStatics.MAX_WIN_TRIGGER) {
                //Only do this in God Mode, not in xGod
                if(!data.featureInfo.ScatterReveal) {
                    scattersTransform[0] = LonerSymbolStates.M;
                    scattersTransform[2] = LonerSymbolStates.A;
                    scattersTransform[4] = LonerSymbolStates.X;
                }
            }
        }
        return scattersTransform;
    }

    /**
     * Returns -1 for none or a positive value for the position on the reel
     * @param data
     * @private
     */
    private parseScatterLanded(data:LonerData):number[] {
        const scattersLanded:number[] = [-1, -1, -1, -1, -1];
        for(let i:number = 0; i < data.reels.length; i++) {
            for(let j:number = 3; j < data.reels[i].length - 3; j++) {
                if(data.reels[i][j] == "S") {
                    scattersLanded[i] = (j - 3);
                }
            }
        }
        return scattersLanded;
    }

    private parseAllScattersLanded():number {
        let scattersLanded:number = 0;
        for(let i:number = 0; i < this._scattersLanded.length; i++) {
            if(this._scattersLanded[i] > -1) {
                scattersLanded++;
            }
        }
        return scattersLanded;
    }

    private parseScatterAnticipationReels():boolean[] {
        const reels:boolean[] = [false, false, false, false, false];

        //No anticipation. You can only get two TVs
        if(Loner.apiPlugin.betFeatureController.getActiveBetFeature()?.name == "BOOSTED_BET_2") {
            return reels;
        }

        if(Loner.apiPlugin.betFeatureController.getActiveBetFeature()?.name == "GOD_MODE") {
            reels[4] = true;
            return reels;
        }

        let counter:number = 0;
        if(this._wasFeatureBuy == false) {
            if(this._allScattersLanded > 1) {
                //Turn on anticipation on all scatters after scatter 2 (unless scatter 2 is on last reel)
                for(let i:number = 0; i < this._scattersLanded.length; i++) {
                    if(this._scattersLanded[i] > -1) {
                        if(i < 4) {
                            counter++;
                        }
                        if(counter > 1) {
                            //Only anticipation on last reel in this game
                            reels[4] = true
                            /*
                            for (let j: number = (i + 1); j < 4; j++) {
                                reels[j] = true;
                            }
                             */
                        }
                    }
                }
            }
        } else {
            const activeFeature:BetFeature | undefined = Loner.apiPlugin.betFeatureController.getActiveBetFeature();
            if(activeFeature && (activeFeature.name == "LUCKY DRAW" || activeFeature.name == "LUCKY_DRAW_2")) {
                //Turn on anticipation on all reels after scatter 3 (unless scatter 3 is on last reel)
                for(let i:number = 0; i < this._scattersLanded.length; i++) {
                    if(this._scattersLanded[i] > -1) {
                        if(i < 4) {
                            counter++;
                        }
                        if(counter > 2) {
                            for(let j:number = (i + 1); j < 5; j++) {
                                reels[j] = true;
                            }
                        }
                    }
                }
            }
        }

        return reels;
    }

    private parseTotalWaysWon(betWayWins:IBetWayWinData[]):void {
        this._totalWaysWon = 0;

        for(let i:number = 0; i < betWayWins.length; i++) {
            this._totalWaysWon += betWayWins[i].numberOfCombinations;
        }
    }

    private parseAllWinSymbolPositions(data:LonerData):void {
        this._allWinSymPositions = [[false, false, false], [false, false, false], [false, false, false], [false, false, false], [false, false, false]];
        if(data.brokeTheBank == false) {
            if(this._betWayWins.length > 0) {
                for(let i:number = 0; i < this._betWayWins.length; i++) {
                    for(let j:number = 0; j < this._betWayWins[i].reelWinPositions.length; j++) {
                        for(let k:number = 0; k < this._betWayWins[i].reelWinPositions[j].length; k++) {
                            if(this._betWayWins[i].reelWinPositions[j][k]) {
                                this._allWinSymPositions[j][k] = true;
                            }
                        }
                    }
                }
            }
        }
    }

    private calculateReelWinPos(evaluatedArea:string[][], symbol:string, length:number):boolean[][] {
        const reelWinPos:boolean[][] = ArrayHelper.initArrayWithValues<boolean[]>(LonerStatics.defaultGameAreaSize.length, (index:number) => {
            return ArrayHelper.initArrayWithValues<boolean>(evaluatedArea[index].length, () => false);
        });
        let connectedReel:boolean = false;
        for(let reelId:number = 0; reelId < length; reelId++) {
            connectedReel = false;
            for(let symId:number = 0; symId < evaluatedArea[reelId].length; symId++) {
                if(evaluatedArea[reelId][symId] == symbol || evaluatedArea[reelId][symId] == "W" || evaluatedArea[reelId][symId] == "XS") {
                    reelWinPos[reelId][symId] = true;
                    connectedReel = true;
                }
            }
            if(!connectedReel) {
                break;
            }
        }
        return reelWinPos;
    }

    //We need an extra row at the top and the bottom because of the stacked symbols
    private addRandomSymbolsToReelset(reels:string[][], isRestore:boolean = false):string[][] {
        for(let i:number = 0; i < reels.length; i++) {
            reels[i].push(this.getRandomSymbolInReelSet());
            reels[i].unshift(this.getRandomSymbolInReelSet());
            reels[i].push(this.getRandomSymbolInReelSet());
            reels[i].unshift(this.getRandomSymbolInReelSet());
            if(reels[i].length == 7) {
                reels[i].push(this.getRandomSymbolInReelSet());
                reels[i].unshift(this.getRandomSymbolInReelSet());
            }
        }
        return reels;
    }

    private getRandomSymbolInReelSet():string {
        const randomIndex:number = Math.floor(Math.random() * LonerModel.RANDOM_REELSET.length);
        return LonerModel.RANDOM_REELSET[randomIndex];
    }

    //If max win, the win amount should only be what's left to reach max win amount
    private parseWinOnMaxWin(data:LonerGameData):void {
        const maxWinAmount:number = data.playedBetValue * 10000;
        this._upToMaxWinAmount = -1;
        if((this._accumulatedRoundWin + data.accumulatedRoundWin) > maxWinAmount) {
            this._upToMaxWinAmount = (maxWinAmount - this._accumulatedRoundWin);
        }
    }

    get betWayWins():IBetWayWinData[] {
        return this._betWayWins;
    }

    get originalDataReels():string[][] {
        return this._originalDataReels;
    }

    get isNaturalMaxWin():boolean {
        if(this._brokeBank == true) {
            if(this.featureTrigger == "Max Win") {
                return false;
            } else {
                return true;
            }
        }
        return false;
    }

    get isRestore():boolean {
        return this._isRestore;
    }

    get winRatio():number {
        if(this._brokeBank) {
            if(this._upToMaxWinAmount > -1) {
                this._winRatio = (this._upToMaxWinAmount / this._playedBetValue);
            } else {
                this._winRatio = (this._accumulatedRoundWin / this._playedBetValue);
            }
        } else if(Loner.model.data.mode == LonerGameMode.NORMAL) {
            this._winRatio = (this._accumulatedRoundWin / this._playedBetValue);
        } else {
            this._winRatio = (this._totalSpinWinnings / this._playedBetValue);
        }

        return this._winRatio;
    }

    get brokeBank():boolean {
        return this._brokeBank;
    }

    get playedBetValue():number {
        return this._playedBetValue;
    }

    get upToMaxWinAmount():number {
        return this._upToMaxWinAmount;
    }

    get accumulatedRoundWin():number {
        return this._accumulatedRoundWin;
    }

    get totalSpinWinnings():number {
        return this._totalSpinWinnings;
    }

    get reels():string[][] {
        return this._reels;
    }

    get allWinSymPositions():boolean[][] {
        return this._allWinSymPositions;
    }

    get totalWaysWon():number {
        return this._totalWaysWon;
    }

    get evaluatedArea():string[][] {
        return this._evaluatedArea;
    }

    get wasFeatureBuy():boolean {
        return this._wasFeatureBuy;
    }

    get freespinsLeft():number {
        return this._freespinsLeft;
    }

    set freespinsLeft(value:number) {
        this._freespinsLeft = value;
    }

    get currentBonusGameWin():number {
        return this._currentBonusGameWin;
    }

    get numberOfFreespinsPlayed():number {
        return this._numberOfFreespinsPlayed;
    }

    get scattersLanded():number[] {
        return this._scattersLanded;
    }

    get allScattersLanded():number {
        return this._allScattersLanded;
    }

    get scatterAnticipationReels():boolean[] {
        return this._scatterAnticipationReels;
    }

    get multiplier():number {
        return this._multiplier;
    }

    get stickyWildMultiplier():number[] {
        return this._stickyWildMultiplier;
    }

    get boostedBetPlayed():boolean {
        return this._boostedBetPlayed;
    }

    get xWaysInfo():xWaysInfo[] {
        return this._xWaysInfo;
    }

    get winMultiplier():number {
        return this._winMultiplier;
    }

    get featureString():number {
        return this._featureString;
    }

    get timesBetWinDraw():number {
        return this._timesBetWinDraw;
    }

    get winRange():string[] {
        return this._winRange;
    }

    get winValues():number[] {
        return this._winValues;
    }

    get featureTrigger():string {
        return this._featureTrigger;
    }

    get preWinPositions():boolean[][] {
        return this._preWinSymbols;
    }

    get scatterTransform():LonerSymbolStates[] {
        return this._scatterTransform;
    }

    get reelsEnteringFreespin():string[][] {
        return this._reelsEnteringFreespin;
    }

    get xSplitInfo():xSplitInfo[] {
        return this._xSplitInfo;
    }

    get duckReels():string[][] {
        return this._duckReels;
    }

    get duckValues():number[][] {
        return this._duckValues;
    }

    get addDucksTopReel():nextDuck[] {
        return this._addDucksTopReel;
    }

    get addDucksMiddleReel():nextDuck[] {
        return this._addDucksMiddleReel;
    }

    get addDucksBottomReel():nextDuck[] {
        return this._addDucksBottomReel;
    }

    get shotData():shotData {
        return this._shotData;
    }

    get totalBonusWin():number {
        return this._totalBonusWin;
    }

    get instantTVReveal():boolean[] {
        return this._instantTVReveal;
    }

    get nextPlayerInteraction():nextPlayerInteraction {
        return this._nextPlayerInteraction;
    }

    get bigWildOnReels():boolean[] {
        return this._bigWildOnReels;
    }

    get pinTheWinFeatureObj() {
        return this._pinTheWinFeatureInfo;
    }

    get mode():string {
        return this._mode;
    }

    get nextMode():string {
        return this._nextMode;
    }

    get bigWildSize():number {
        return this._bigWildSize;
    }

    get bigWildReelPosition():number {
        return this._bigWildReelPosition;
    }

    get bigWildWinAnimationHighlightData():BigWildWinAnimationHighlightData {
        return this._bigWildWinAnimationHighlightData;
    }

    get featureName():string {
        return this._featureName;
    }

    set featureName(value:string) {
        this._featureName = value;
    }

    get symbolSizes():number[][] {
        return this._symbolSizes;
    }
}